import { ReactElement } from 'react';

import { IconProps } from '@components/icons';
import { IconAttachment } from '@components/icons/attachment';
import { IconBrandNationalRegistry } from '@components/icons/brand-national-registry';
import { IconCompany } from '@components/icons/company';
import { IconEmail } from '@components/icons/email';
import { IconFile } from '@components/icons/file';
import { IconForm } from '@components/icons/form';
import { IconPersonDetails } from '@components/icons/icon-person-details';
import { IconJob } from '@components/icons/job';
import { IconPerson } from '@components/icons/person';
import { IconPhone } from '@components/icons/phone';
import { IconSelectMultiple } from '@components/icons/select-multiple';
import { IconSelectSingle } from '@components/icons/select-single';
import { IconTextFormatNumber } from '@components/icons/text-format-number';
import { IconTextLong } from '@components/icons/text-long';
import { IconTextShort } from '@components/icons/text-short';
import {
  FieldMappingFieldItem,
  FieldMappingGroup,
  FieldType,
} from '@components/text-editors/addons/field-mapping';
import { FIELD_MAPPING_ICON_COLOR } from '@components/text-editors/addons/field-mapping-type-maps';
import { CareerSiteLanguageType } from '@locale/locale-store';
import { useScopedTranslate } from '@locale/translate';

import { useGetJob } from 'api/jobs/hooks';
import { FLAG_MAPPER_SMALL } from 'components/iconMappers';
import { useAuthenticatedUser } from 'features/authentication';
import { useStore } from 'models/Provider';
import { JobQuestion, JobQuestionType } from 'types/jobs';

import { filterVariableGroupsByTypes } from './field-config/variable-groups';

function useJobFields(jobLanguages: CareerSiteLanguageType[]) {
  const { t } = useScopedTranslate(locale);
  const types = [
    {
      label: t('jobTitle'),
      field: 'title',
    },
    {
      label: t('shortDescription'),
      field: 'short_description',
    },
    {
      label: t('longDescription'),
      field: 'description',
    },
  ];
  const jobFields: FieldMappingFieldItem[] = [];
  types.forEach((type) => {
    jobLanguages.forEach((language) => {
      jobFields.push({
        icon: <></>,
        iconRight: FLAG_MAPPER_SMALL[language],
        label: `${type.label} • ${language.toUpperCase()}`,
        source: 'job',
        field: `${type.field}__${language}`,
        type: 'text',
      });
    });
  });
  return jobFields;
}

function useFormFields(
  jobLanguages: CareerSiteLanguageType[],
  form: {
    sections: {
      id: string;
      questions: JobQuestion[];
    }[];
  },
) {
  const questionMap: Record<
    JobQuestionType,
    { icon: ReactElement<IconProps>; type: FieldType; shouldTranslate: boolean }
  > = {
    one_line: {
      icon: <IconTextShort color={FIELD_MAPPING_ICON_COLOR} />,
      type: 'text',
      shouldTranslate: false,
    },
    multi_line: {
      icon: <IconTextLong color={FIELD_MAPPING_ICON_COLOR} />,
      type: 'text',
      shouldTranslate: false,
    },
    dropdown: {
      icon: <IconSelectSingle color={FIELD_MAPPING_ICON_COLOR} />,
      type: 'text',
      shouldTranslate: true,
    },
    multidropdown: {
      icon: <IconSelectMultiple color={FIELD_MAPPING_ICON_COLOR} />,
      type: 'array',
      shouldTranslate: true,
    },
    attachment: {
      icon: <IconAttachment color={FIELD_MAPPING_ICON_COLOR} />,
      type: 'file',
      shouldTranslate: false,
    },
  };
  const formFields: FieldMappingFieldItem[] = [];
  (form.sections ?? []).forEach((section) => {
    section.questions.forEach((question) => {
      const mapObject = questionMap[question.type];
      const fieldType = question.multiple ? 'file_multi' : mapObject.type;
      if (mapObject.shouldTranslate) {
        jobLanguages.forEach((language) => {
          formFields.push({
            icon: mapObject.icon,
            iconRight: FLAG_MAPPER_SMALL[language],
            label: `${question.title} • ${language.toUpperCase()}`,
            source: 'form',
            field: `${question.id}__${language}`,
            type: fieldType,
          });
        });
      } else {
        formFields.push({
          icon: mapObject.icon,
          label: question.title,
          source: 'form',
          field: question.id,
          type: fieldType,
        });
      }
    });
  });
  return formFields;
}

export const useVariableGroups = ({
  jobId,
  types = undefined,
}: {
  /** null = global (company-settings) context: job + form groups are omitted. */
  jobId: number | null;
  types?: FieldType[] | undefined;
}): FieldMappingGroup[] => {
  const { t } = useScopedTranslate(locale);
  const jobQuery = useGetJob(jobId ?? 0, jobId !== null);
  const me = useAuthenticatedUser();
  const userLanguage = me.language;
  const { current } = useStore();
  const { useSsnRegistry } = current.info;
  const jobLanguages = jobQuery.data?.languages?.map((lang) => lang.language) ?? ['en'];
  const jobForm =
    jobQuery.data?.languages.find((lang) => lang.language === userLanguage)?.form ??
    jobQuery.data?.form;

  const baseVariableGroups: FieldMappingGroup[] = [
    {
      id: 'application',
      icon: <IconPerson color={FIELD_MAPPING_ICON_COLOR} />,
      label: t('applicantDetails'),
      fields: [
        {
          icon: <IconTextFormatNumber color={FIELD_MAPPING_ICON_COLOR} />,
          label: t('applicantId'),
          source: 'application',
          field: 'id',
          type: 'number',
        },
        {
          icon: <IconPersonDetails color={FIELD_MAPPING_ICON_COLOR} />,
          label: t('applicantFullName'),
          source: 'application',
          field: 'name',
          type: 'text',
        },
        {
          icon: <IconPersonDetails color={FIELD_MAPPING_ICON_COLOR} />,
          label: t('applicantFirstName'),
          source: 'application',
          field: 'first_name',
          type: 'text',
        },
        {
          icon: <IconPersonDetails color={FIELD_MAPPING_ICON_COLOR} />,
          label: t('applicantLastName'),
          source: 'application',
          field: 'last_name',
          type: 'text',
        },
        {
          icon: <IconEmail color={FIELD_MAPPING_ICON_COLOR} />,
          label: t('applicationEmail'),
          source: 'application',
          field: 'email',
          type: 'email',
        },
        {
          icon: <IconPhone color={FIELD_MAPPING_ICON_COLOR} />,
          label: t('applicationPhone'),
          source: 'application',
          field: 'phone',
          type: 'phone',
        },
        {
          icon: <IconPersonDetails color={FIELD_MAPPING_ICON_COLOR} />,
          label: t('ssn'),
          source: 'application',
          field: 'ssn',
          type: 'text',
        },
        {
          icon: <IconFile color={FIELD_MAPPING_ICON_COLOR} />,
          label: t('cv'),
          source: 'application',
          field: 'cv',
          type: 'file',
        },
        {
          icon: <IconFile color={FIELD_MAPPING_ICON_COLOR} />,
          label: t('coverLetter'),
          source: 'application',
          field: 'cover_letter',
          type: 'file',
        },
      ],
    },
    {
      id: 'form',
      icon: <IconForm color={FIELD_MAPPING_ICON_COLOR} />,
      label: t('applicationForm'),
      fields: useFormFields(jobLanguages, jobForm ?? { sections: [] }),
    },
    useSsnRegistry && {
      id: 'ssnregistry',
      icon: <IconBrandNationalRegistry size={24} color={FIELD_MAPPING_ICON_COLOR} />,
      label: t('nationalRegistry'),
      fields: [
        {
          icon: <></>,
          label: t('fullName'),
          source: 'ssnregistry',
          field: 'name',
          type: 'text',
        },
        {
          icon: <></>,
          label: t('ssnIdNumber'),
          source: 'ssnregistry',
          field: 'ssn',
          type: 'text',
        },
        {
          icon: <></>,
          label: t('age'),
          source: 'ssnregistry',
          field: 'age',
          type: 'text',
        },
        {
          icon: <></>,
          label: t('ageEndOfYear'),
          source: 'ssnregistry',
          field: 'age_year_end',
          type: 'text',
        },
        {
          icon: <></>,
          label: t('street'),
          source: 'ssnregistry',
          field: 'street',
          type: 'text',
        },
        {
          icon: <></>,
          label: t('city'),
          source: 'ssnregistry',
          field: 'city',
          type: 'text',
        },
        {
          icon: <></>,
          label: t('postalCode'),
          source: 'ssnregistry',
          field: 'postal_code',
          type: 'text',
        },
        {
          icon: <></>,
          label: t('genderCode'),
          source: 'ssnregistry',
          field: 'gender_value',
          type: 'text',
        },
        ...(jobLanguages.includes('en')
          ? [
              {
                icon: <></>,
                label: t('genderLabel'),
                source: 'ssnregistry',
                field: 'gender_label__en',
                type: 'text',
                iconRight: FLAG_MAPPER_SMALL['en'],
              },
            ]
          : []),
        ...(jobLanguages.includes('is')
          ? [
              {
                icon: <></>,
                label: t('genderLabel'),
                source: 'ssnregistry',
                field: 'gender_label__is',
                type: 'text',
                iconRight: FLAG_MAPPER_SMALL['is'],
              },
            ]
          : []),
      ],
    },
    {
      id: 'job',
      icon: <IconJob color={FIELD_MAPPING_ICON_COLOR} />,
      label: t('jobDetails'),
      fields: useJobFields(jobLanguages),
    },
    {
      id: 'company',
      icon: <IconCompany color={FIELD_MAPPING_ICON_COLOR} />,
      label: t('company'),
      fields: [
        {
          icon: <></>,
          label: t('companyName'),
          source: 'company',
          field: 'company_name',
          type: 'text',
        },
        {
          icon: <></>,
          label: t('companyWebsiteUrl'),
          source: 'company',
          field: 'url',
          type: 'text',
        },
      ],
    },
  ];
  return filterVariableGroupsByTypes(
    baseVariableGroups.filter(
      (group) => Boolean(group) && (jobId !== null || (group.id !== 'job' && group.id !== 'form')),
    ),
    types,
  );
};

const locale = {
  en: {
    applicantDetails: 'Applicant details',
    applicantId: 'Application ID',
    applicationEmail: 'Applicant email',
    applicationPhone: 'Applicant phone',
    applicantFullName: 'Applicant full name',
    applicantFirstName: 'Applicant first name',
    applicantLastName: 'Applicant last name',
    ssn: 'SSN',
    applicationForm: 'Application form',
    nationalRegistry: 'National registry',
    fullName: 'Full name',
    ssnIdNumber: 'SSN / ID Number',
    age: 'Age',
    ageEndOfYear: 'Age at end of year',
    street: 'Street',
    city: 'City',
    postalCode: 'Postal code',
    genderCode: 'Gender • code',
    genderLabel: 'Gender • name',
    jobDetails: 'Job details',
    jobTitle: 'Job title',
    shortDescription: 'Short description',
    longDescription: 'Long description',
    company: 'Company',
    companyName: 'Company name',
    companyWebsiteUrl: 'Company website url',
    cv: 'CV',
    coverLetter: 'Cover letter',
  },
  is: {
    applicantDetails: 'Upplýsingar umsækjanda',
    applicantId: 'Númer umsækjanda',
    applicationEmail: 'Netfang umsækjanda',
    applicationPhone: 'Sími umsækjanda',
    applicantFullName: 'Fullt nafn umsækjanda',
    applicantFirstName: 'Fornafn umsækjanda',
    applicantLastName: 'Eftirnafn umsækjanda',
    ssn: 'Kennitala',
    applicationForm: 'Umsóknarform',
    nationalRegistry: 'Þjóðskrá',
    fullName: 'Fullt nafn',
    ssnIdNumber: 'Kennitala',
    age: 'Aldur',
    ageEndOfYear: 'Aldur í lok árs',
    street: 'Gata',
    city: 'Borg',
    postalCode: 'Póstnúmer',
    genderCode: 'Kyn • kóði',
    genderLabel: 'Kyn • heiti',
    jobDetails: 'Starfsauglýsing',
    jobTitle: 'Starfstitill',
    shortDescription: 'Stutt starfslýsing',
    longDescription: 'Löng starfslýsing',
    company: 'Fyrirtæki',
    companyName: 'Nafn fyrirtækis',
    companyWebsiteUrl: 'Vefsíða fyrirtækis',
    cv: 'Ferilskrá',
    coverLetter: 'Kynningarbréf',
  },
};
